/**
 * StreamingMarkdown — drives a Layer 4 streaming generation and shows a
 * progress view while it runs.
 *
 * The component opens the SSE stream to a Layer 4 streaming route, reads it
 * to completion, and shows a progress card the whole time — "Generating… +
 * elapsed timer + a filling bar". It does NOT paint the Blueprint text as it
 * streams. When the stream finishes it calls `onDone(payload)` with the
 * `done` event's payload, and the parent page renders the polished Blueprint.
 *
 * History: the streaming surfaces used to render the text live — first in
 * 150ms batches (the StreamingMarkdownLite cheap renderer, bug #64), briefly
 * as a typewriter. The live render was a persistent source of trouble. On
 * 2026-05-25 every streaming surface (the live-demo tool + the Pre-Meeting /
 * Resume / Relational pages) moved to this progress-bar loading state,
 * matching the BlueprintJobView progress card the Tier-2 surfaces use.
 *
 * The SSE transport is still required: a Layer 4 Blueprint runs 30–50s and a
 * plain request would exceed Netlify's ~26s function timeout. The stream
 * keeps the connection alive; this component just doesn't paint the deltas.
 *
 * Usage:
 *   <StreamingMarkdown
 *     // IMPORTANT: pass the V2 path (the function's
 *     // `export const config = { path: "/api/<X>/stream" }`), NOT the V1
 *     // default — a V2 custom-path function doesn't bind the V1 default,
 *     // so the V1 URL 404s to Netlify's SPA fallback. Bug #40 (2026-05-06).
 *     url="/api/pre-meeting/stream"
 *     bearerToken={session.access_token}
 *     body={{ ... }}
 *     onDone={(payload) => setBlueprint(payload)}
 *     onRetry={stream.retry}                  // optional — Regenerate affordance
 *     surfaceLabel="Pre-Meeting Blueprint"    // optional — progress-card label
 *   />
 *
 * Props are unchanged from the streaming-render version, so every caller
 * works without modification. `fieldName` is still accepted for backward
 * compatibility but is no longer used (it selected the live-render field).
 */

import { useEffect, useState, useRef } from "react";
import { Callout } from "./index.js";
import { parseSseChunk } from "../lib/sse-parser.js";

// Elapsed-timer tick interval.
const ELAPSED_TICK_MS = 1000;

// Soft completion estimate for the progress-bar fill. A streaming Layer 4
// Blueprint runs ~30–50s; the bar eases toward 90% across this window and
// only completes when `done` actually arrives, so it never over-promises.
const PROGRESS_ESTIMATE_SECONDS = 45;

export default function StreamingMarkdown({
  url,
  bearerToken,
  body,
  onDone,
  onRetry,
  surfaceLabel = "Blueprint",
  progressLine = "Drafting the sections — building the full briefing.",
  // eslint-disable-next-line no-unused-vars -- accepted for back-compat; the
  // live-render field selector was removed with the progress-view rewrite.
  fieldName,
}) {
  const [phase, setPhase] = useState("connecting"); // connecting | streaming | done | error
  const [error, setError] = useState(null);
  // When the upstream emits a typed brand-discipline error, render a friendly
  // retry affordance instead of the raw diagnostic. Task #62.
  const [errorType, setErrorType] = useState(null);
  const [errorDetail, setErrorDetail] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const abortRef = useRef(null);

  // ── SSE lifecycle ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!url || !bearerToken) return;
    const controller = new AbortController();
    abortRef.current = controller;
    setErrorType(null);
    setErrorDetail(null);
    setElapsedSeconds(0);

    // Auto-retry a single transient failure BEFORE the first delta (Russ
    // 2026-05-09). fetch() throws on transient blips — Anthropic API
    // hiccups, function cold starts, momentary connection resets. A single
    // 800ms retry catches most of them transparently. Once a delta has
    // arrived the generation is genuinely under way, so a later failure
    // surfaces normally instead of silently retrying.
    const MAX_PREDELTA_ATTEMPTS = 2;
    let attempt = 0;

    (async () => {
      while (attempt < MAX_PREDELTA_ATTEMPTS) {
        attempt += 1;
        let sawAnyDelta = false;
        try {
          setPhase("connecting");
          const res = await fetch(url, {
            method: "POST",
            headers: {
              "content-type": "application/json",
              authorization: `Bearer ${bearerToken}`,
            },
            body: JSON.stringify(body || {}),
            signal: controller.signal,
          });
          if (!res.ok || !res.body) {
            // HTTP error before any stream body — auto-retry once on
            // 502/503/504 (transient gateway failures); surface
            // immediately on 4xx (caller error, retry won't fix).
            if (attempt < MAX_PREDELTA_ATTEMPTS && [502, 503, 504].includes(res.status)) {
              await new Promise((r) => setTimeout(r, 800));
              continue;
            }
            const errBody = await res.text();
            setError(errBody.slice(0, 500));
            setPhase("error");
            return;
          }

          setPhase("streaming");
          const reader = res.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";
          let sawDone = false;
          let sawError = false;

          while (true) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });

            const { events, remainingBuffer } = parseSseChunk(buffer);
            buffer = remainingBuffer;
            for (const ev of events) {
              if (ev.type === "delta") {
                // Deltas keep the connection alive and confirm generation
                // is under way; the progress view does not paint them.
                sawAnyDelta = true;
              } else if (ev.type === "done") {
                sawDone = true;
                setPhase("done");
                if (onDone) onDone(ev.data);
              } else if (ev.type === "error") {
                sawError = true;
                setError(ev.data.message || "Stream error");
                // Typed-error fields ride on the same event when the
                // upstream tags it (currently: brand_discipline_trip from
                // _lib/blueprint-stream.js). When present, the render path
                // swaps the diagnostic Watch Out for a friendly Regenerate
                // affordance. Task #62.
                if (ev.data.error_type) setErrorType(ev.data.error_type);
                if (ev.data.detail) setErrorDetail(ev.data.detail);
                setPhase("error");
              }
            }
          }

          // The reader closed without a terminal SSE event. Most common
          // cause: the upstream Netlify Function was killed before it could
          // emit `done` or `error` (idle-timeout, runtime cap). Bug #47.
          //
          // Pre-delta-silent-close: auto-retry once — the upstream may have
          // cold-started and dropped the connection before emitting any SSE.
          //
          // Post-delta-silent-close: surface immediately — a genuine
          // mid-stream interrupt usually means the function exhausted its
          // runtime cap, which won't change on retry.
          if (!sawDone && !sawError) {
            if (!sawAnyDelta && attempt < MAX_PREDELTA_ATTEMPTS) {
              await new Promise((r) => setTimeout(r, 800));
              continue;
            }
            setError("The generation ended unexpectedly before it completed. Try regenerating.");
            setPhase("error");
          }
          // Stream completed normally (or a terminal error was received) —
          // exit the retry loop.
          return;
        } catch (e) {
          if (e.name === "AbortError") return;
          // Pre-delta thrown error (network blip, fetch failure): auto-retry
          // once with a short backoff. After the first delta, surface
          // immediately.
          if (!sawAnyDelta && attempt < MAX_PREDELTA_ATTEMPTS) {
            await new Promise((r) => setTimeout(r, 800));
            continue;
          }
          setError(e.message);
          setPhase("error");
          return;
        }
      }
    })();

    return () => {
      controller.abort();
    };
  }, [url, bearerToken, JSON.stringify(body)]);

  // ── Live elapsed-timer tick while the generation is in flight ──────────
  useEffect(() => {
    if (phase !== "connecting" && phase !== "streaming") return;
    const id = setInterval(() => setElapsedSeconds((s) => s + 1), ELAPSED_TICK_MS);
    return () => clearInterval(id);
  }, [phase]);

  // ── Render: error ──────────────────────────────────────────────────────
  if (phase === "error") {
    return (
      <div data-component="StreamingMarkdown" data-phase="error">
        {errorType === "brand_discipline_trip" && onRetry ? (
          <Callout.WatchOut>
            <strong>We caught some internal vocabulary in this draft.</strong>{" "}
            Regenerate to try again.
            <div style={{ marginTop: 12 }}>
              <button
                type="button"
                onClick={onRetry}
                data-testid="streaming-markdown-regenerate"
                style={primaryRetryButtonStyle}
              >
                Regenerate
              </button>
            </div>
          </Callout.WatchOut>
        ) : (
          <Callout.WatchOut>
            <strong>Generation interrupted.</strong> {error}
            {onRetry && (
              <div style={{ marginTop: 12 }}>
                <button type="button" onClick={onRetry} style={ghostRetryButtonStyle}>
                  Try again
                </button>
              </div>
            )}
          </Callout.WatchOut>
        )}
      </div>
    );
  }

  // ── Render: progress card (connecting / streaming / brief done) ────────
  const pct = Math.min(
    90,
    Math.round((elapsedSeconds / Math.max(1, PROGRESS_ESTIMATE_SECONDS)) * 90),
  );
  return (
    <div data-component="StreamingMarkdown" data-phase={phase}>
      <div style={progressCardStyle}>
        <div style={progressEyebrowStyle}>Generating {surfaceLabel}…</div>
        <p style={progressLineStyle}>
          <strong>{progressLine}</strong>
        </p>
        <p style={progressMetaStyle}>
          {formatElapsed(elapsedSeconds)} elapsed · typically under a minute
        </p>
        <div style={progressTrackStyle}>
          <div
            style={{
              ...progressFillStyle,
              width: `${phase === "done" ? 100 : pct}%`,
            }}
          />
        </div>
      </div>
    </div>
  );
}

// Pure helper — seconds → "Ns" / "Nm Ns".
function formatElapsed(seconds) {
  const s = Math.max(0, Math.floor(seconds || 0));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
}

// ── Styles — the progress card mirrors BlueprintJobView's card grammar ───
const progressCardStyle = {
  padding: "20px 22px",
  border: "1px solid var(--ppr-color-border-default)",
  background: "var(--ppr-color-background-surface)",
};

const progressEyebrowStyle = {
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: "11px",
  letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
  textTransform: "uppercase",
  color: "var(--ppr-color-text-muted)",
  marginBottom: 8,
};

const progressLineStyle = {
  margin: "0 0 12px",
  fontFamily: "var(--ppr-typography-font-family-body)",
  fontSize: "var(--ppr-typography-font-size-base)",
  lineHeight: 1.55,
  color: "var(--ppr-color-text-primary)",
};

const progressMetaStyle = {
  margin: "0 0 16px",
  fontFamily: "var(--ppr-typography-font-family-mono)",
  fontSize: 13,
  color: "var(--ppr-color-text-muted)",
};

const progressTrackStyle = {
  height: 4,
  background: "var(--ppr-color-background-muted)",
  borderRadius: 2,
  overflow: "hidden",
};

const progressFillStyle = {
  height: "100%",
  background: "var(--ppr-color-accent-navy)",
  transition: "width 1s linear",
};

const primaryRetryButtonStyle = {
  fontFamily: "var(--ppr-typography-font-family-ui)",
  fontSize: "var(--ppr-typography-font-size-sm)",
  fontWeight: 600,
  padding: "8px 16px",
  background: "var(--ppr-color-accent-navy)",
  color: "var(--ppr-color-text-inverse)",
  border: "1px solid var(--ppr-color-accent-navy)",
  borderRadius: "var(--ppr-radius-sm)",
  cursor: "pointer",
};

const ghostRetryButtonStyle = {
  fontFamily: "var(--ppr-typography-font-family-ui)",
  fontSize: "var(--ppr-typography-font-size-sm)",
  fontWeight: 600,
  padding: "8px 16px",
  background: "var(--ppr-color-background-surface)",
  color: "var(--ppr-color-text-default)",
  border: "1px solid var(--ppr-color-border-default)",
  borderRadius: "var(--ppr-radius-sm)",
  cursor: "pointer",
};
